import { isNonEmpty } from "@-/com.npmjs.ts-array-length";
import onetime from "@-/com.npmjs.onetime";
import { getPinyinSyllables } from "@-/dev.improve.haniverse.lib.morpheme.grapheme.pinyin";

/** Search also accepts CC-CEDICT's standalone erhua suffix r5. */
const getSearchPinyinSyllables = onetime(
  (): ReadonlySet<string> => new Set([...getPinyinSyllables(), "r"]),
);

// Syllables are typed apart with spaces or apostrophes ("xi'an"), or a hyphen.
const PINYIN_SYLLABLE_SEPARATOR_REGEX = /[\s'-]+/;

// A syllable, optionally followed by its tone digit (0 or 5 for the neutral tone).
const PINYIN_TOKEN_REGEX = /^([a-z]+)([0-5])?$/;

/**
 * Whether some syllable starts with `text` (lowercase, toneless): "zh" and "zhi" do, while a final
 * such as "iu" or "ong" only ends one.
 */
export function startsPinyinSyllable(text: string): boolean {
  if (text.length === 0) {
    return false;
  }
  for (const syllable of getSearchPinyinSyllables()) {
    if (syllable.startsWith(text)) {
      return true;
    }
  }
  return false;
}

/**
 * Whether a query reads as pinyin, so Search matches it against readings by default.
 *
 * Every syllable must be a real one, optionally with a tone digit ("zhi", "zhi4", "huo3 che1"),
 * except that the last may be unfinished ("zh", "zhon"): the learner is still typing it. English
 * words that happen to spell a syllable ("you", "she", "fan") are pinyin too, which is why Search
 * lets the learner turn pinyin matching off for the current search, as it lets them turn it on for
 * text that is not detected, such as a final ("iu").
 */
export function isPinyinSearchQuery(query: string): boolean {
  const tokens = query
    .trim()
    .toLocaleLowerCase()
    .split(PINYIN_SYLLABLE_SEPARATOR_REGEX)
    .filter((token) => token.length > 0);
  if (!isNonEmpty(tokens)) {
    return false;
  }
  const lastIndex = tokens.length - 1;
  return tokens.every((token, index) => {
    const match = PINYIN_TOKEN_REGEX.exec(token);
    if (match === null) {
      return false;
    }
    const [, syllable = "", tone] = match;
    if (getSearchPinyinSyllables().has(syllable)) {
      return true;
    }
    return index === lastIndex && tone === undefined && startsPinyinSyllable(syllable);
  });
}
